import { Button, Kind } from '@thanx/uikit/button'
import { RadioGroup } from '@thanx/uikit/radio'
import { Text } from '@thanx/uikit/text'
import { createCampaign } from 'actions/campaigns'
import { alert } from 'actions/flash'
import RichSelector from 'components/Form/RichSelector'
import Book from 'components/Icons/Book'
import BullHornRectangleIcon from 'components/Icons/BullHornRectangle'
import BullsEyeRectangleIcon from 'components/Icons/BullsEyeRectangle'
import RotateRectangleIcon from 'components/Icons/RotateRectangle'
import Modal from 'components/ModalContainer'
import { PermissionTooltip } from 'components/Navbar/Bar/PermissionTooltip'
import useCanMessageEveryone from 'hooks/useCanMessageEveryone'
import useDispatch from 'hooks/useDispatch'
import { usePermissionReason } from 'hooks/useRbacNavCheck'
import { buildTranslate } from 'locales'
import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { push } from 'react-router-redux'
import { useAbility } from 'utilities/ability'

const t = buildTranslate('thanx_campaigns.newCampaign')

type CampaignType = 'targeted' | 'message' | 'automated'

interface RouteConfig {
  campaignPath: string
  campaignType: string
}

const ROUTES: Record<CampaignType, RouteConfig> = {
  automated: {
    campaignType: 'custom_automated',
    campaignPath: '/thanx_campaigns/automated',
  },
  message: {
    campaignType: 'message',
    campaignPath: '/thanx_campaigns/message/message',
  },
  targeted: {
    campaignType: 'custom',
    campaignPath: '/thanx_campaigns/targeted',
  },
}

type Props = {
  isOpen: boolean
  onClose: () => void
}

const NewCampaignModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const dispatch = useDispatch()
  const ability = useAbility()
  const campaignsDisabled = ability.cannot('manage', 'AllCampaigns')
  const canMessageEveryone = useCanMessageEveryone()
  const messageDisabled = campaignsDisabled || !canMessageEveryone

  const firstAvailable: CampaignType | undefined = !campaignsDisabled
    ? 'targeted'
    : undefined
  const [selected, setSelected] = useState<CampaignType>()
  const campaignsReason = usePermissionReason('AllCampaigns', campaignsDisabled)
  const messageReason =
    messageDisabled && !campaignsDisabled
      ? t('message.admin_or_global_required')
      : campaignsReason
  // Sync selection when abilities load or change
  const effectiveSelected = selected ?? firstAvailable

  async function handleContinue() {
    if (!effectiveSelected) return
    if (effectiveSelected === 'message' && messageDisabled) return
    if (effectiveSelected === 'message') {
      dispatch(push(ROUTES[effectiveSelected].campaignPath))
      onClose()
      return
    }

    setIsSubmitting(true)

    const params = {
      campaign: {
        type: ROUTES[effectiveSelected].campaignType,
      },
    }

    const action = await dispatch(createCampaign(params))
    if (action.error) {
      dispatch(
        alert({
          key: 'danger',
          message: t('error_creating_campaign'),
        })
      )
      setIsSubmitting(false)
      return
    }

    const campaignId = action.payload?.data?.campaign?.id
    if (campaignId) {
      const { campaignPath, campaignType } = ROUTES[effectiveSelected]
      const path = `${campaignPath}/${campaignType}/${campaignId}`
      dispatch(push(path))
    } else {
      dispatch(
        alert({
          key: 'danger',
          message: t('error_creating_campaign'),
        })
      )
    }
    setIsSubmitting(false)
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      title={t('title')}
      onClose={onClose}
      onProceed={handleContinue}
      proceedText={t('get_started')}
      canProceed={
        !isSubmitting &&
        !!effectiveSelected &&
        !(effectiveSelected === 'message' && messageDisabled)
      }
      buttonGroupChildren={
        <div className="flex-1">
          <Link
            to={{
              pathname:
                'https://help.thanx.com/hc/en-us/categories/9020361665303-Marketing',
            }}
            target="_blank"
          >
            <Button kind={Kind.MINIMAL} size="small" className="p-0">
              {t('learn_more')}
              <Book className="ml-xs" />
            </Button>
          </Link>
        </div>
      }
    >
      <RadioGroup value={effectiveSelected} className="mt-xl">
        <RichSelector
          disabled={campaignsDisabled}
          value="targeted"
          image={<BullsEyeRectangleIcon />}
          imageClassName="ml-xs"
          className="mb-s"
          onClick={() => setSelected('targeted')}
        >
          <PermissionTooltip
            disabled={campaignsDisabled}
            reason={campaignsReason}
            id="new-campaign-targeted-tooltip"
            placement="top"
          >
            <Text.Header4 bold className="mt-0">
              {t('targeted.title')}
            </Text.Header4>
            <Text.BodyText4 tag="p" className="mb-0" color="grey70">
              {t('targeted.description')}
            </Text.BodyText4>
          </PermissionTooltip>
        </RichSelector>

        <RichSelector
          disabled={messageDisabled}
          value="message"
          image={<BullHornRectangleIcon />}
          imageClassName="ml-xs"
          className="mb-s"
          onClick={() => setSelected('message')}
        >
          <PermissionTooltip
            disabled={messageDisabled}
            reason={messageReason}
            id="new-campaign-message-tooltip"
            placement="top"
          >
            <Text.Header4 bold className="mt-0">
              {t('message.title')}
            </Text.Header4>
            <Text.BodyText4 tag="p" className="mb-0" color="grey70">
              {t('message.description')}
            </Text.BodyText4>
          </PermissionTooltip>
        </RichSelector>

        <RichSelector
          disabled={campaignsDisabled}
          value="automated"
          image={<RotateRectangleIcon />}
          imageClassName="ml-xs"
          onClick={() => setSelected('automated')}
        >
          <PermissionTooltip
            disabled={campaignsDisabled}
            reason={campaignsReason}
            id="new-campaign-automated-tooltip"
            placement="top"
          >
            <Text.Header4 bold className="mt-0">
              {t('automated.title')}
            </Text.Header4>
            <Text.BodyText4 tag="p" className="mb-0" color="grey70">
              {t('automated.description')}
            </Text.BodyText4>
          </PermissionTooltip>
        </RichSelector>
      </RadioGroup>
    </Modal>
  )
}

export default NewCampaignModal
